import {
  ConsentCategory,
  UserClientApp,
} from "@amzn/client-workbench-api-model";
import { API } from "@aws-amplify/api";

import { API_METRICS_HANDLE } from "@/apis/constants";
import { Logger } from "@/apis/Logger";
import {
  publishQueryLatencyMetrics,
  publishQueryResultMetrics,
} from "@/apis/Metrics";

import { AMPLIFY_DEFAULT_API_NAME } from "../amplifyConfig";

/**
 * Request schema for updating user consent (v2)
 */
export interface UpdateUserConsentV2RequestSchema {
  consentCategory: ConsentCategory;
  consentGranted: boolean;
  termsVersion: string;
  clientApp?: UserClientApp;
}

/**
 * Response schema for user consent update (v2)
 */
export interface UpdateUserConsentV2ResponseSchema {
  personId: string;
  consentCategory: ConsentCategory;
  consentGranted: boolean;
  termsVersion: string;
  eventTime: string;
}

export const SUBMIT_USER_CONSENT_V2_QUERY_KEY = "SubmitUserConsentV2";
export const SUBMIT_USER_CONSENTS_V2_API_PATH = "/user/consents/v2";

/**
 * Submit user consent for a specific category.
 *
 * @param consentCategory - The category of consent to submit
 * @param consentGranted - Whether the user granted consent
 * @param termsVersion - Version of consent terms (default: "1.0")
 * @returns Promise<UpdateUserConsentV2ResponseSchema> - Consent response
 */
export const submitUserConsentV2 = async (
  consentCategory: ConsentCategory,
  consentGranted: boolean,
  termsVersion = "1.0"
): Promise<UpdateUserConsentV2ResponseSchema> => {
  const startTime = new Date().getTime();

  try {
    const requestBody: UpdateUserConsentV2RequestSchema = {
      consentCategory,
      consentGranted,
      termsVersion,
      clientApp: "DayOneOnboardingWizard",
    } as const;

    const response: UpdateUserConsentV2ResponseSchema = (await API.post(
      AMPLIFY_DEFAULT_API_NAME,
      SUBMIT_USER_CONSENTS_V2_API_PATH,
      {
        body: requestBody,
      }
    )) as UpdateUserConsentV2ResponseSchema;

    publishQueryResultMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: SUBMIT_USER_CONSENT_V2_QUERY_KEY,
      numSuccess: 1,
      numFailures: 0,
    });

    return response;
  } catch (err) {
    void Logger.errorErrorLike(`Failed to store user consent.`, err);
    publishQueryResultMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: SUBMIT_USER_CONSENT_V2_QUERY_KEY,
      numSuccess: 0,
      numFailures: 1,
    });
    throw err;
  } finally {
    const elapsedTime = new Date().getTime() - startTime;
    publishQueryLatencyMetrics({
      metricsPublisherHandler: API_METRICS_HANDLE,
      queryFunctionName: SUBMIT_USER_CONSENT_V2_QUERY_KEY,
      elapsedTime: elapsedTime,
    });
  }
};
